const API_BASE = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5001/api' : '/api');
const TOKEN_KEY = 'genieai_token';

export async function login(username, password, remember = false) {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username: username.trim(), password })
  });
  if (!res.ok) throw new Error('Invalid credentials');
  const data = await res.json();
  // Validate token shape before persisting so isAuthenticated() never crashes
  // on a truncated/empty server response.
  if (!data || typeof data.token !== 'string' || data.token.split('.').length !== 3) {
    throw new Error('Login succeeded but returned an invalid token');
  }
  // Remember me: localStorage (persists), else sessionStorage (closes with tab)
  const storage = remember ? localStorage : sessionStorage;
  storage.setItem(TOKEN_KEY, data.token);
  if (remember) localStorage.setItem('genieai_remember', 'true');
  else localStorage.removeItem('genieai_remember');
  return data;
}

export async function logout() {
  const token = getToken();
  if (token) {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch {}
  }
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem('genieai_remember');
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
}

export function isAuthenticated() {
  const token = getToken();
  if (!token) return false;
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return false;
  try {
    const payload = JSON.parse(atob(parts[1]));
    return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now();
  } catch { return false; }
}

// Decode JWT payload (no verification — server is the authority on auth).
// Returns { username, role, exp } or null on any failure.
export function getCurrentUser() {
  const token = getToken();
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    const payload = JSON.parse(atob(parts[1]));
    return {
      username: payload.username || null,
      role: payload.role || 'retail',
      menu_access: Array.isArray(payload.menu_access) ? payload.menu_access : null,
      exp: payload.exp,
    };
  } catch { return null; }
}

export function getRole() { return getCurrentUser()?.role || null; }
export function isAdmin() { return getRole() === 'admin'; }

// Live menu_access stored in localStorage, refreshed from /api/auth/me on app
// boot and after the admin edits their own access rights. Falls back to the
// JWT payload value when the fresh fetch hasn't happened yet.
const MENU_ACCESS_KEY = 'genie.menu_access';

export function getMenuAccess() {
  try {
    const raw = localStorage.getItem(MENU_ACCESS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      if (parsed === null) return null;
    }
  } catch { /* fall through */ }
  return getCurrentUser()?.menu_access ?? null;
}

export function setMenuAccess(menuAccess) {
  try {
    localStorage.setItem(MENU_ACCESS_KEY, JSON.stringify(menuAccess ?? null));
    // Notify same-window listeners (storage event only fires across windows).
    window.dispatchEvent(new CustomEvent('menuAccessChanged'));
  } catch { /* ignore */ }
}

export async function refreshMenuAccess() {
  const token = getToken();
  if (!token) return null;
  try {
    const base = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
    const res = await fetch(`${base}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    setMenuAccess(data.menu_access ?? null);
    return data.menu_access ?? null;
  } catch { return null; }
}
